import { type RefObject, useCallback, useEffect, useRef } from "react";

type UseInfiniteScrollOptions = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Re-evaluates the underfill check whenever this changes (e.g. loaded item count). */
  itemCount?: number;
  /** Distance in px from the bottom of the scroll container at which to prefetch the next page. */
  threshold?: number;
};

/**
 * Threshold-based infinite scroll for a scrollable container. Attach the returned
 * `scrollRef`/`onScroll` to the element that owns `overflow-y-auto`. Fetches the next
 * page when the user nears the bottom, and also auto-fetches while the loaded content
 * is too short to fill (and therefore scroll) the container.
 */
export const useInfiniteScroll = <T extends HTMLElement = HTMLDivElement>({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  itemCount,
  threshold = 200,
}: UseInfiniteScrollOptions): { scrollRef: RefObject<T | null>; onScroll: () => void } => {
  const scrollRef = useRef<T>(null);

  const maybeFetchNextPage = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, threshold]);

  // Keep loading while the content doesn't fill the container (no scrollbar to reach).
  // React Aria's Table commits its rows in a render *after* this effect (its collection
  // updates via useSyncExternalStore), so measuring synchronously can read a container
  // that's still empty and fetch a page nobody scrolled to. Measure on the next frame,
  // once the new rows are in the DOM and laid out.
  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(maybeFetchNextPage);
    });
    return () => cancelAnimationFrame(frame);
  }, [maybeFetchNextPage, itemCount]);

  return { scrollRef, onScroll: maybeFetchNextPage };
};
